import { useRef, type ReactNode } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import usePrefersReducedMotion from "../hooks/usePrefersReducedMotion";

interface ParallaxLayerProps {
  children: ReactNode;
  /** How far the layer drifts across its own scroll range, in px. Negative = slower than scroll (background feel). */
  offset?: number;
  className?: string;
}

/**
 * Wraps a background element (e.g. a hero image) and gives it a subtle
 * vertical drift as its own section scrolls through the viewport, so it
 * moves at a different speed than the foreground content stacked on top of
 * it — the classic parallax-depth cue (ZP-92 AC1). Transform-only (no
 * layout properties touched), so it can't contribute to CLS.
 */
export default function ParallaxLayer({ children, offset = -60, className }: ParallaxLayerProps) {
  const ref = useRef<HTMLDivElement>(null);
  const reducedMotion = usePrefersReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, offset]);

  if (reducedMotion) {
    return (
      <div ref={ref} className={className}>
        {children}
      </div>
    );
  }

  return (
    <div ref={ref} className={className}>
      <motion.div style={{ y }} className="h-full w-full">
        {children}
      </motion.div>
    </div>
  );
}
